import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { SupabaseService } from './supabase.service';
import { FirebaseTravelDataService } from './firebase-travel-data.service';
import type {
  TravelBookingRow,
  TravelCustomerRow,
  TravelDashboardStats,
  TravelDriverRow,
  TravelExpenseRow,
  TravelPaymentRow,
  TravelRentalRow,
  TravelServiceRow,
  TravelStorefrontContent,
  TravelVehicleDocumentRow,
  TravelVehicleRateRow,
  TravelVehicleRow
} from '../../megha/business-travels/travel.types';

@Injectable({ providedIn: 'root' })
export class TravelManagementService {
  private readonly supabase = inject(SupabaseService);
  private readonly firebase = inject(FirebaseTravelDataService);

  /** Firebase is default for Book Ride; set `environment.travelDataBackend = 'supabase'` to rollback. */
  usesFirebase(): boolean {
    const kind = (environment as { travelDataBackend?: string }).travelDataBackend;
    if (kind === 'supabase') return false;
    // Configuration errors must not silently send Firebase users to another database.
    return true;
  }

  async getDashboardStats(
    storeCode: string
  ): Promise<{ data: TravelDashboardStats | null; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.getDashboardStats(storeCode);
    const { data, error } = await this.supabase.client.rpc('get_travel_dashboard_stats', {
      p_store_code: storeCode.trim()
    });
    if (error) return { data: null, error: error.message };
    const row = data as { success?: boolean; message?: string } & TravelDashboardStats;
    if (!row?.success) return { data: null, error: row?.message || 'Failed to load stats.' };
    return {
      data: {
        total_vehicles: Number(row.total_vehicles ?? 0),
        available_vehicles: Number(row.available_vehicles ?? 0),
        active_bookings: Number(row.active_bookings ?? 0),
        today_bookings: Number(row.today_bookings ?? 0),
        monthly_revenue: Number(row.monthly_revenue ?? 0),
        pending_payments: Number(row.pending_payments ?? 0)
      },
      error: null
    };
  }

  async seedDefaultServices(storeCode: string): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.seedDefaultServices(storeCode);
    const { data, error } = await this.supabase.client.rpc('seed_travel_default_services', {
      p_store_code: storeCode.trim()
    });
    if (error) return { error: error.message };
    const row = data as { success?: boolean; message?: string };
    return { error: row?.success ? null : row?.message || 'Seed failed.' };
  }

  async listVehicles(storeCode: string): Promise<{ data: TravelVehicleRow[]; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.listVehicles(storeCode);
    const { data, error } = await this.supabase.client
      .from('store_travel_vehicles')
      .select('*')
      .eq('store_code', storeCode.trim())
      .order('sort_order')
      .order('title');
    if (error) return { data: [], error: error.message };
    return { data: (data || []).map((r) => this.normalizeVehicleRow(r)), error: null };
  }

  async upsertVehicle(
    storeCode: string,
    vehicle: Partial<TravelVehicleRow> & { title: string }
  ): Promise<{ data: TravelVehicleRow | null; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.upsertVehicle(storeCode, vehicle);
    const payload = {
      store_code: storeCode.trim(),
      sort_order: vehicle.sort_order ?? 0,
      title: vehicle.title.trim(),
      short_description: vehicle.short_description?.trim() || null,
      image_url: vehicle.image_url?.trim() || null,
      image_urls: vehicle.image_urls || [],
      vehicle_brand: vehicle.vehicle_brand?.trim() || null,
      vehicle_type: vehicle.vehicle_type?.trim() || null,
      manufacture_year: vehicle.manufacture_year ?? null,
      fuel_type: vehicle.fuel_type?.trim() || null,
      transmission: vehicle.transmission?.trim() || null,
      seating_capacity: vehicle.seating_capacity ?? null,
      daily_price_inr: vehicle.daily_price_inr ?? null,
      supports_booking: vehicle.supports_booking !== false,
      supports_rental: vehicle.supports_rental !== false,
      is_active: vehicle.is_active !== false,
      extra_json: vehicle.extra_json || {}
    };
    const query = vehicle.id
      ? this.supabase.client
          .from('store_travel_vehicles')
          .update(payload)
          .eq('id', vehicle.id)
          .eq('store_code', storeCode.trim())
          .select('*')
          .single()
      : this.supabase.client.from('store_travel_vehicles').insert(payload).select('*').single();
    const { data, error } = await query;
    if (error) return { data: null, error: error.message };
    return { data: this.normalizeVehicleRow(data), error: null };
  }

  async deleteVehicle(storeCode: string, vehicleId: string): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.deleteVehicle(storeCode, vehicleId);
    const { error } = await this.supabase.client
      .from('store_travel_vehicles')
      .delete()
      .eq('store_code', storeCode.trim())
      .eq('id', vehicleId);
    return { error: error?.message || null };
  }

  async listVehicleRates(
    storeCode: string,
    vehicleId?: string
  ): Promise<{ data: TravelVehicleRateRow[]; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.listVehicleRates(storeCode, vehicleId);
    let q = this.supabase.client
      .from('store_travel_vehicle_rates')
      .select('*')
      .eq('store_code', storeCode.trim());
    if (vehicleId) q = q.eq('vehicle_id', vehicleId);
    const { data, error } = await q.order('service_type');
    if (error) return { data: [], error: error.message };
    return { data: (data || []) as TravelVehicleRateRow[], error: null };
  }

  async upsertVehicleRate(
    storeCode: string,
    rate: Partial<TravelVehicleRateRow> & { vehicle_id: string; service_type: string }
  ): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.upsertVehicleRate(storeCode, rate);
    const payload = {
      store_code: storeCode.trim(),
      vehicle_id: rate.vehicle_id,
      service_type: rate.service_type,
      base_price_inr: rate.base_price_inr ?? null,
      per_km_price_inr: rate.per_km_price_inr ?? null,
      minimum_km: rate.minimum_km ?? null,
      driver_charge_inr: rate.driver_charge_inr ?? null,
      night_charge_inr: rate.night_charge_inr ?? null
    };
    const query = rate.id
      ? this.supabase.client
          .from('store_travel_vehicle_rates')
          .update(payload)
          .eq('id', rate.id)
          .eq('store_code', storeCode.trim())
      : this.supabase.client.from('store_travel_vehicle_rates').insert(payload);
    const { error } = await query;
    return { error: error?.message || null };
  }

  async deleteVehicleRate(storeCode: string, rateId: string): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.deleteVehicleRate(storeCode, rateId);
    const { error } = await this.supabase.client
      .from('store_travel_vehicle_rates')
      .delete()
      .eq('store_code', storeCode.trim())
      .eq('id', rateId);
    return { error: error?.message || null };
  }

  async listVehicleDocuments(
    storeCode: string,
    vehicleId?: string
  ): Promise<{ data: TravelVehicleDocumentRow[]; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.listVehicleDocuments(storeCode, vehicleId);
    let q = this.supabase.client
      .from('store_travel_vehicle_documents')
      .select('*')
      .eq('store_code', storeCode.trim());
    if (vehicleId) q = q.eq('vehicle_id', vehicleId);
    const { data, error } = await q.order('document_type');
    if (error) return { data: [], error: error.message };
    return { data: (data || []) as TravelVehicleDocumentRow[], error: null };
  }

  async upsertVehicleDocument(
    storeCode: string,
    docRow: Partial<TravelVehicleDocumentRow> & {
      vehicle_id: string;
      document_type: string;
      document_url: string;
    }
  ): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.upsertVehicleDocument(storeCode, docRow);
    const payload = {
      store_code: storeCode.trim(),
      vehicle_id: docRow.vehicle_id,
      document_type: docRow.document_type,
      document_url: docRow.document_url.trim(),
      expiry_date: docRow.expiry_date || null
    };
    const query = docRow.id
      ? this.supabase.client
          .from('store_travel_vehicle_documents')
          .update(payload)
          .eq('id', docRow.id)
          .eq('store_code', storeCode.trim())
      : this.supabase.client.from('store_travel_vehicle_documents').insert(payload);
    const { error } = await query;
    return { error: error?.message || null };
  }

  async listServices(storeCode: string): Promise<{ data: TravelServiceRow[]; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.listServices(storeCode);
    const { data, error } = await this.supabase.client
      .from('store_travel_services')
      .select('*')
      .eq('store_code', storeCode.trim())
      .order('sort_order');
    if (error) return { data: [], error: error.message };
    return { data: (data || []) as TravelServiceRow[], error: null };
  }

  async upsertService(
    storeCode: string,
    svc: Partial<TravelServiceRow> & { name: string; service_type: string }
  ): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.upsertService(storeCode, svc);
    const payload = {
      store_code: storeCode.trim(),
      name: svc.name.trim(),
      description: svc.description?.trim() || null,
      service_type: svc.service_type,
      icon: svc.icon?.trim() || null,
      is_active: svc.is_active !== false,
      sort_order: svc.sort_order ?? 0
    };
    const query = svc.id
      ? this.supabase.client
          .from('store_travel_services')
          .update(payload)
          .eq('id', svc.id)
          .eq('store_code', storeCode.trim())
      : this.supabase.client.from('store_travel_services').insert(payload);
    const { error } = await query;
    return { error: error?.message || null };
  }

  async deleteService(storeCode: string, id: string): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.deleteService(storeCode, id);
    const { error } = await this.supabase.client
      .from('store_travel_services')
      .delete()
      .eq('store_code', storeCode.trim())
      .eq('id', id);
    return { error: error?.message || null };
  }

  async listDrivers(storeCode: string): Promise<{ data: TravelDriverRow[]; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.listDrivers(storeCode);
    const { data, error } = await this.supabase.client
      .from('store_travel_drivers')
      .select('*')
      .eq('store_code', storeCode.trim())
      .order('name');
    if (error) return { data: [], error: error.message };
    return { data: (data || []) as TravelDriverRow[], error: null };
  }

  async upsertDriver(
    storeCode: string,
    driver: Partial<TravelDriverRow> & { name: string; phone: string }
  ): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.upsertDriver(storeCode, driver);
    const payload = {
      store_code: storeCode.trim(),
      name: driver.name.trim(),
      phone: driver.phone.trim(),
      license_number: driver.license_number?.trim() || null,
      experience_years: driver.experience_years ?? null,
      driver_type: driver.driver_type || 'own_driver',
      is_available: driver.is_available !== false,
      status: driver.status || 'active'
    };
    const query = driver.id
      ? this.supabase.client
          .from('store_travel_drivers')
          .update(payload)
          .eq('id', driver.id)
          .eq('store_code', storeCode.trim())
      : this.supabase.client.from('store_travel_drivers').insert(payload);
    const { error } = await query;
    return { error: error?.message || null };
  }

  async deleteDriver(storeCode: string, id: string): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.deleteDriver(storeCode, id);
    const { error } = await this.supabase.client
      .from('store_travel_drivers')
      .delete()
      .eq('store_code', storeCode.trim())
      .eq('id', id);
    return { error: error?.message || null };
  }

  async listCustomers(storeCode: string): Promise<{ data: TravelCustomerRow[]; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.listCustomers(storeCode);
    const { data, error } = await this.supabase.client
      .from('store_travel_customers')
      .select('*')
      .eq('store_code', storeCode.trim())
      .order('created_at', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: (data || []) as TravelCustomerRow[], error: null };
  }

  async upsertCustomer(
    storeCode: string,
    customer: Partial<TravelCustomerRow> & { phone: string }
  ): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.upsertCustomer(storeCode, customer);
    const payload = {
      store_code: storeCode.trim(),
      name: customer.name?.trim() || null,
      phone: customer.phone.trim(),
      email: customer.email?.trim() || null,
      address: customer.address?.trim() || null,
      city: customer.city?.trim() || null,
      village: customer.village?.trim() || null
    };
    const query = customer.id
      ? this.supabase.client
          .from('store_travel_customers')
          .update(payload)
          .eq('id', customer.id)
          .eq('store_code', storeCode.trim())
      : this.supabase.client.from('store_travel_customers').insert(payload);
    const { error } = await query;
    return { error: error?.message || null };
  }

  async listBookings(storeCode: string): Promise<{ data: TravelBookingRow[]; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.listBookings(storeCode);
    const { data, error } = await this.supabase.client
      .from('store_travel_bookings')
      .select('*')
      .eq('store_code', storeCode.trim())
      .order('created_at', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: (data || []) as TravelBookingRow[], error: null };
  }

  async updateBooking(
    storeCode: string,
    bookingId: string,
    patch: Partial<TravelBookingRow>
  ): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.updateBooking(storeCode, bookingId, patch);
    const payload: Record<string, unknown> = { ...patch };
    if (patch.booking_status) payload['status'] = patch.booking_status;
    const { error } = await this.supabase.client
      .from('store_travel_bookings')
      .update(payload)
      .eq('store_code', storeCode.trim())
      .eq('id', bookingId);
    return { error: error?.message || null };
  }

  async updateBookingStatus(
    storeCode: string,
    bookingId: string,
    status: string
  ): Promise<{ error: string | null }> {
    return this.updateBooking(storeCode, bookingId, { booking_status: status, status });
  }

  async listRentals(storeCode: string): Promise<{ data: TravelRentalRow[]; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.listRentals(storeCode);
    const { data, error } = await this.supabase.client
      .from('store_travel_rentals')
      .select('*')
      .eq('store_code', storeCode.trim())
      .order('created_at', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: (data || []) as TravelRentalRow[], error: null };
  }

  async updateRental(
    storeCode: string,
    rentalId: string,
    patch: Partial<TravelRentalRow>
  ): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.updateRental(storeCode, rentalId, patch);
    const payload: Record<string, unknown> = { ...patch };
    if (patch.rental_status) payload['status'] = patch.rental_status;
    const { error } = await this.supabase.client
      .from('store_travel_rentals')
      .update(payload)
      .eq('store_code', storeCode.trim())
      .eq('id', rentalId);
    return { error: error?.message || null };
  }

  async updateRentalStatus(
    storeCode: string,
    rentalId: string,
    status: string
  ): Promise<{ error: string | null }> {
    return this.updateRental(storeCode, rentalId, { rental_status: status, status });
  }

  async listPayments(storeCode: string): Promise<{ data: TravelPaymentRow[]; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.listPayments(storeCode);
    const { data, error } = await this.supabase.client
      .from('store_travel_payments')
      .select('*')
      .eq('store_code', storeCode.trim())
      .order('paid_at', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: (data || []) as TravelPaymentRow[], error: null };
  }

  async recordPayment(
    storeCode: string,
    payment: Partial<TravelPaymentRow> & { amount: number }
  ): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.recordPayment(storeCode, payment);
    const { error } = await this.supabase.client.from('store_travel_payments').insert({
      store_code: storeCode.trim(),
      booking_id: payment.booking_id || null,
      rental_id: payment.rental_id || null,
      amount: payment.amount,
      payment_type: payment.payment_type || 'full',
      payment_mode: payment.payment_mode || 'cash',
      status: payment.status || 'success',
      transaction_id: payment.transaction_id?.trim() || null,
      paid_at: payment.paid_at || new Date().toISOString()
    });
    return { error: error?.message || null };
  }

  async listExpenses(storeCode: string): Promise<{ data: TravelExpenseRow[]; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.listExpenses(storeCode);
    const { data, error } = await this.supabase.client
      .from('store_travel_expenses')
      .select('*')
      .eq('store_code', storeCode.trim())
      .order('expense_date', { ascending: false });
    if (error) return { data: [], error: error.message };
    return { data: (data || []) as TravelExpenseRow[], error: null };
  }

  async upsertExpense(
    storeCode: string,
    expense: Partial<TravelExpenseRow> & {
      expense_type: string;
      amount: number;
      expense_date: string;
    }
  ): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.upsertExpense(storeCode, expense);
    const payload = {
      store_code: storeCode.trim(),
      vehicle_id: expense.vehicle_id || null,
      expense_type: expense.expense_type,
      amount: expense.amount,
      description: expense.description?.trim() || null,
      expense_date: expense.expense_date
    };
    const query = expense.id
      ? this.supabase.client
          .from('store_travel_expenses')
          .update(payload)
          .eq('id', expense.id)
          .eq('store_code', storeCode.trim())
      : this.supabase.client.from('store_travel_expenses').insert(payload);
    const { error } = await query;
    return { error: error?.message || null };
  }

  async deleteExpense(storeCode: string, id: string): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.deleteExpense(storeCode, id);
    const { error } = await this.supabase.client
      .from('store_travel_expenses')
      .delete()
      .eq('store_code', storeCode.trim())
      .eq('id', id);
    return { error: error?.message || null };
  }

  async getStorefrontContent(
    storeCode: string
  ): Promise<{ data: TravelStorefrontContent; error: string | null }> {
    if (this.usesFirebase()) return this.firebase.getStorefrontContent(storeCode);
    const { data, error } = await this.supabase.client
      .from('store_travel_storefront')
      .select('content')
      .eq('store_code', storeCode.trim())
      .maybeSingle();
    if (error) return { data: {}, error: error.message };
    return { data: (data?.content as TravelStorefrontContent) || {}, error: null };
  }

  async saveStorefrontContent(
    storeCode: string,
    content: TravelStorefrontContent
  ): Promise<{ error: string | null }> {
    if (this.usesFirebase()) return this.firebase.saveStorefrontContent(storeCode, content);
    const { data, error } = await this.supabase.client.rpc('upsert_travel_storefront', {
      p_store_code: storeCode.trim(),
      p_content: content
    });
    if (error) return { error: error.message };
    const row = data as { success?: boolean; message?: string };
    return { error: row?.success ? null : row?.message || 'Failed to save storefront.' };
  }

  private normalizeVehicleRow(raw: Record<string, unknown>): TravelVehicleRow {
    return this.firebase.normalizeVehicle(raw);
  }
}
